interface ApiSharedConfig {
  baseURL?: string;
  appKey?: string;
  apiKey?: string;
  xWidget?: string;
}

interface StoredAuthData {
  appKey: string;
  apiKey: string;
}

interface ApiSharedState {
  keyPromises: Map<string, Promise<string>>;
}

const AUTH_DATA_STORAGE_KEY = 'authData:apiKeys';
const DIRECTORIES_STORAGE_KEY = 'directories';
const LEGACY_DIRECTORIES_STORAGE_KEY = 'authData:directories';
const SHARED_STATE_KEY = '__rezbotApiSharedState';

// Safe Storage Helper to prevent crashes in iOS/Safari Private Mode or Cross-Origin iFrames
const memoryFallback = new Map();

function safeGetItem(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return memoryFallback.get(key) || null;
  }
}

function safeSetItem(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    memoryFallback.set(key, value);
  }
}

function safeRemoveItem(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    memoryFallback.delete(key);
  }
}

// Module-level state
let apiConfig: ApiSharedConfig = {};
const keyPromises = getApiSharedState().keyPromises;

export function configureApiShared(config: ApiSharedConfig) {
  apiConfig = { ...apiConfig, ...config };
}

export async function getApiKey() {
  const { baseURL, appKey, apiKey, xWidget } = getRequiredConfig();
  const storageKey = getStorageKey(appKey);

  // 1. Instant return for late-loading widgets (Check sessionStorage / safe storage first)
  const storedAuthData = getStoredAuthData(appKey);
  if (storedAuthData) {
    return storedAuthData.apiKey;
  }

  // 2. Return existing request for simultaneous/in-flight calls
  if (!keyPromises.has(storageKey)) {
    const headers: HeadersInit = {
      'X-API-KEY': apiKey,
      'X-APP-KEY': appKey
    };

    if (xWidget) {
      headers['X-WIDGET'] = xWidget;
    }

    const keyPromise = fetch(`${trimTrailingSlash(baseURL)}/auths?access=public`, { headers })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load key');
        return res.text();
      })
      .then(text => JSON.parse(cleanPhpErrors(text)))
      .then(data => {
        const token = data?.auths?.jwt;

        if (!token) throw new Error('Auth response did not include a public key');

        storeSharedDirectories(data?.directories);

        return {
          appKey,
          apiKey: token
        };
      })
      .then(data => {
        // 3. Store only the app/public-key pair. Directories are common across auth entries.
        storeAuthData(data);
        
        // Clear promise reference now that data is stored
        keyPromises.delete(storageKey);
        return data.apiKey;
      })
      .catch(err => {
        // Clear promise reference on failure so subsequent attempts can retry
        keyPromises.delete(storageKey);
        throw err;
      });

    keyPromises.set(storageKey, keyPromise);
  }

  return keyPromises.get(storageKey);
}

// Optional: Helper to retrieve full stored auth payload synchronously
export function getStoredAuthData(appKey = getRequiredConfig().appKey): StoredAuthData | null {
  return getStoredAuthDataList().find(data => data.appKey === appKey) || null;
}

export function storeSharedDirectories(directories: any): void {
  if (!directories) return;

  const storedDirectories = getSharedDirectories() || {};
  const mergedDirectories = { ...storedDirectories, ...normaliseDirectories(directories) };

  safeSetItem(DIRECTORIES_STORAGE_KEY, JSON.stringify(mergedDirectories));
  safeRemoveItem(LEGACY_DIRECTORIES_STORAGE_KEY);
}

export function getSharedDirectories(): any {
  const data = safeGetItem(DIRECTORIES_STORAGE_KEY);

  if (!data) return null;

  try {
    return normaliseDirectories(JSON.parse(data));
  } catch (e) {
    safeRemoveItem(DIRECTORIES_STORAGE_KEY);

    return null;
  }
}

// Optional: Helper to clear key upon receiving 401/403 status codes
export function invalidateApiKey() {
  const { appKey } = getRequiredConfig();
  const storageKey = getStorageKey(appKey);
  const authData = getStoredAuthDataList().filter(data => data.appKey !== appKey);

  safeSetItem(AUTH_DATA_STORAGE_KEY, JSON.stringify(authData));
  keyPromises.delete(storageKey);
}

function getRequiredConfig(): Required<Pick<ApiSharedConfig, 'baseURL'|'appKey'|'apiKey'>> & Pick<ApiSharedConfig, 'xWidget'> {
  if (!apiConfig.baseURL || !apiConfig.appKey || !apiConfig.apiKey) {
    throw new Error('API shared storage is missing baseURL, appKey, or apiKey');
  }

  return {
    baseURL: apiConfig.baseURL,
    appKey: apiConfig.appKey,
    apiKey: apiConfig.apiKey,
    xWidget: apiConfig.xWidget
  };
}

function getStoredAuthDataList(): StoredAuthData[] {
  const data = safeGetItem(AUTH_DATA_STORAGE_KEY);

  if (!data) return [];

  try {
    const parsedData = JSON.parse(data);

    if (Array.isArray(parsedData)) return parsedData;
    if (parsedData?.appKey && parsedData?.apiKey) return [parsedData];

    return [];
  } catch (e) {
    safeRemoveItem(AUTH_DATA_STORAGE_KEY);

    return [];
  }
}

function storeAuthData(authData: StoredAuthData): void {
  const storedAuthData = getStoredAuthDataList().filter(data => data.appKey !== authData.appKey);

  safeSetItem(AUTH_DATA_STORAGE_KEY, JSON.stringify([...storedAuthData, authData]));
}

function normaliseDirectories(directories: any): any {
  if (!directories || typeof directories !== 'object') return {};
  if (!Array.isArray(directories)) return directories;

  return directories.reduce((data, directory) => {
    if (!directory || typeof directory !== 'object') return data;

    const directoryName = directory.directory || directory.name || directory.key || directory.type;
    const directoryValue = directory.response ?? directory.data ?? directory.body ?? directory.items ?? directory.value;

    if (directoryName && directoryValue !== undefined) return { ...data, [directoryName]: directoryValue };

    return { ...data, ...directory };
  }, {});
}

function getApiSharedState(): ApiSharedState {
  const globalScope = globalThis as any;

  if (!globalScope[SHARED_STATE_KEY]) {
    globalScope[SHARED_STATE_KEY] = {
      keyPromises: new Map<string, Promise<string>>()
    };
  }

  return globalScope[SHARED_STATE_KEY];
}

function getStorageKey(appKey: string): string {
  return ['authData', appKey].join(':');
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function cleanPhpErrors(responseText: string): string {
  if (!responseText) return responseText;

  const jsonStartRegex = /^[\s\S]*?([{\[])/;
  const match = responseText.match(jsonStartRegex);

  if (!match) return responseText;

  return responseText.substring(responseText.indexOf(match[1]));
}